import { ElementRef, Injectable } from '@angular/core';
import { FaceMesh } from '@mediapipe/face_mesh';

// Thresholds
const BlinkThreshold: number = 0.110; //0.28
const GeneralDeephThreshold: number = 0.000125;
const ThresholdNoseCheeks: number = 0.1600;
const ThresholdNoseChin: number = 0.08;
const ThresholdNoseForehead: number = 0.022;
export const PupilThreshold: number = 0.0045;
export const MouthThreshold: number = 0.000060;
export const BrightnessThreshold:number = 105;
const maxFrames: number = 10;

@Injectable({
  providedIn: 'root'
})
export class FaceMeshService {
  faceMeshLoaded:boolean = false;
  public faceMesh: FaceMesh | null = null;
  // Variables de estado
  private framesLandmarks: any[][] = [];
  private acceptedPartialDeeph: boolean[] = [];       //La profundida de cierta partes del rostro
  private acceptedGeneralDeeph: boolean[] = [];       //La profundidad de todo el rostro
  acceptedGeneralDeephScored: number[] = [];
  blinkThreshold:number = BlinkThreshold;
  blinkedTimes: boolean[] = [];
  blinkedScores:number[] = [];
  brightnessScores:number[] = [];
  acceptableExpressionForCapture: boolean = false;

  constructor() {}

  async initFaceMesh() {
    this.faceMesh = new FaceMesh({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
      // locateFile: (file) => `/assets/mediapipe/face_mesh/${file}`
    });
    this.faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5
    });
    await this.faceMesh.initialize();
    this.faceMeshLoaded = true;
    console.log("✅ Modelos facemesh cargado.", this.faceMesh);
    this.warmupDetection();
  }

  warmupDetection(){
    try {
      // Crear canvas dummy de 50x50
      const dummyCanvas = document.createElement('canvas');
      dummyCanvas.width = 50;
      dummyCanvas.height = 50;
      const ctx = dummyCanvas.getContext('2d');
      if(ctx) {
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, 50, 50);
      }
      
      // Ejecutar detección fantasma
      if(this.faceMesh) this.faceMesh.send({ image: dummyCanvas }).then(()=> console.log("✅ FaceMesh WebGL warmed up"));
    } catch (error) {
      console.debug('Error en warmup FaceMesh:', error);
    }
  }

  async isRealFaceWithDepth(correctFraming: boolean, videoElement:ElementRef<HTMLVideoElement>): Promise<any> {
    return new Promise((resolve) => {
      this.faceMesh?.onResults((results: any) => {
        if (!results.multiFaceLandmarks || results.multiFaceLandmarks.length === 0) {
          resolve({});
          return;
        }

        const brightness = this.calculateFrameBrightness(videoElement.nativeElement);
        this.brightnessScores.push(brightness)
        const acceptableBrightness = brightness > BrightnessThreshold;
        const landmarks = results.multiFaceLandmarks[0];
        const blinked = this.detectBlinkFaceMesh(landmarks);
        this.blinkedTimes.push(blinked);

        if (correctFraming){
          this.acceptableExpressionForCapture = !blinked;
          this.storeLandmarks(landmarks);
          
          const depthScore: number = this.calculateDepthVariance(landmarks);
          this.acceptedGeneralDeephScored.push(depthScore);

          if (depthScore < GeneralDeephThreshold) {
            this.acceptedGeneralDeeph.push(false);
          } else {
            this.acceptedGeneralDeeph.push(true);
          }

          // Usar un punto de referencia para normalizar, por ejemplo, la punta de la nariz (landmark 1 o 4).
          const noseTipReferenceZ = landmarks[4].z; 

          const normalizedNoseTipZ = landmarks[1].z - noseTipReferenceZ;
          const normalizedLeftCheekZ = landmarks[234].z - noseTipReferenceZ;
          const normalizedRightCheekZ = landmarks[454].z - noseTipReferenceZ;
          const normalizedChinZ = landmarks[152].z - noseTipReferenceZ;
          const normalizedForeheadZ = landmarks[10].z - noseTipReferenceZ;

          const depthNoseToCheeks = Math.abs(normalizedNoseTipZ - (normalizedLeftCheekZ + normalizedRightCheekZ) / 2);
          const depthNoseToChin = Math.abs(normalizedNoseTipZ - normalizedChinZ);
          const depthNoseToForehead = Math.abs(normalizedNoseTipZ - normalizedForeheadZ);
          
          // CONSOLE LOGS PARA CALIBRACIÓN (eliminar en producción):
          // console.log(`Normalized Depths: Nose-Cheeks: ${depthNoseToCheeks.toFixed(4)}, Nose-Chin: ${depthNoseToChin.toFixed(4)}, Nose-Forehead: ${depthNoseToForehead.toFixed(4)}`);

          const checkNoseToCheeks = depthNoseToCheeks >= ThresholdNoseCheeks; 
          const checkNoseToChin = depthNoseToChin >= ThresholdNoseChin;
          const checkNoseToForehead = depthNoseToForehead >= ThresholdNoseForehead;

          // Lógica de decisión:
          // Opción 1: Todas deben cumplirse (como estaba antes)
          // let acceptablePartialsDepths = checkNoseToCheeks && checkNoseToChin && checkNoseToForehead;

          // Opción 2: Al menos 1 de 3 deben cumplirse (más flexible)
          const conditionsMet = [checkNoseToCheeks, checkNoseToChin, checkNoseToForehead].filter(c => c).length;
          let acceptablePartialsDepths = conditionsMet >= 1;

          this.storePartialDeephs(acceptablePartialsDepths && acceptableBrightness);
          resolve({ acceptablePartialsDepths, acceptableBrightness });
        } else {
          resolve({});
        }
      });

      if (videoElement && videoElement.nativeElement && videoElement.nativeElement.readyState >= 2) { // Check if video is ready
        try {
          this.faceMesh?.send({ image: videoElement.nativeElement });
        } catch (error) {
          resolve({});
        }
      } else {
        resolve({});
      }
    });
  }

    /**
   * Calcula el brillo promedio de un frame de video.
   * Retorna un valor entre 0 (negro) y 255 (blanco).
   */
  calculateFrameBrightness(video: HTMLVideoElement): number {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let sum = 0;
    for (let i = 0; i < frame.data.length; i += 4) {
      // Promedio de R, G, B
      sum += (frame.data[i] + frame.data[i + 1] + frame.data[i + 2]) / 3;
    }
    return sum / (frame.data.length / 4);
  }

  // Calculate depth variance
  calculateDepthVariance(landmarks: any[]): number {
    // 1. Normalizar valores Z relativos a la nariz
    const noseZ = landmarks[4].z; // Landmark de la punta de la nariz
    const normalizedZ = landmarks.map(point => point.z - noseZ);
  
    // 2. Calcular varianza en regiones clave
    const regions = {
      nose: [4, 1, 5, 6, 168], // Nariz y puente nasal
      leftCheek: [234, 227, 137, 177, 215],
      rightCheek: [454, 447, 366, 397, 435],
      forehead: [10, 151, 337, 299, 333],
      jaw: [152, 148, 176, 149, 150]
    };
  
    // 3. Calcular varianza por región
    const regionVariances = Object.values(regions).map(regionIndices => {
      const zValues = regionIndices.map(i => normalizedZ[i]);
      const mean = zValues.reduce((a, b) => a + b) / zValues.length;
      return zValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / zValues.length;
    });
  
    // 4. Calcular relación nariz-mejilla
    const noseCheekDiff = Math.abs(
      normalizedZ[4] - (normalizedZ[234] + normalizedZ[454]) / 2
    );
  
    // 5. Factor de profundidad dinámico
    const dynamicDepthFactor = Math.max(
      0.5, // Valor mínimo
      1 - Math.exp(-noseCheekDiff * 10) // Ajuste exponencial
    );
  
    // 6. Ponderar varianzas con factores anatómicos
    const weights = [0.4, 0.15, 0.15, 0.2, 0.1]; // Priorizar nariz, frente
    const weightedVariance = regionVariances.reduce((sum, val, i) => sum + val * weights[i], 0);
    // console.log('Varianza de profundidad ',weightedVariance * dynamicDepthFactor)
    return weightedVariance * dynamicDepthFactor;
  }

  // Calculate dynamic threshold
  calculateDynamicThreshold(landmarks: any[][]): number {
    const referenceValues = landmarks.map(l => {
      const noseZ = l[4].z;
      return Math.abs(l[234].z - noseZ + l[454].z - noseZ) / 2;
    });
    const avgReference = referenceValues.reduce((a, b) => a + b) / referenceValues.length;
    return Math.max(0.002, 0.0005 * Math.exp(avgReference * 5));
  }

  detectBlinkFaceMesh(faceLandmarks: any): boolean {
    const rightEye = [
      faceLandmarks[33], faceLandmarks[160], faceLandmarks[158],
      faceLandmarks[133], faceLandmarks[153], faceLandmarks[144]
    ];
    const leftEye = [
      faceLandmarks[263], faceLandmarks[387], faceLandmarks[385],
      faceLandmarks[362], faceLandmarks[380], faceLandmarks[373]
    ];

    const rightEAR = this.calculateEAR(rightEye);
    const leftEAR = this.calculateEAR(leftEye);
    const avgEAR = (rightEAR + leftEAR) / 2;
    this.blinkedScores.push(avgEAR)
    return avgEAR < this.blinkThreshold; // Umbral de parpadeo
  }

  getHowManyTimesBlinked():number{
    return this.blinkedTimes.filter((b)=> b).length;
  }

  getBlinkedScores():number[]{
    return this.blinkedScores;
  }

  // Check if eyes are open
  areEyesOpen(faceLandmarks: any): boolean {
    // Usamos los mismos landmarks que en la detección de parpadeo
    const rightEye = [
      faceLandmarks[33], faceLandmarks[160], faceLandmarks[158],
      faceLandmarks[133], faceLandmarks[153], faceLandmarks[144]
    ];
    const leftEye = [
      faceLandmarks[263], faceLandmarks[387], faceLandmarks[385],
      faceLandmarks[362], faceLandmarks[380], faceLandmarks[373]
    ];

    // Calculamos EAR para ambos ojos
    const rightEAR = this.calculateEAR(rightEye);
    const leftEAR = this.calculateEAR(leftEye);
    const avgEAR = (rightEAR + leftEAR) / 2;

    // Umbrales ajustados para ojos abiertos (mayor que parpadeo pero dentro de rango normal)
    const MIN_OPEN_THRESHOLD = 0.20;  // Ajustar según testing
    const MAX_OPEN_THRESHOLD = 0.60;  // Previene valores anormalmente altos
    // Verificamos que el EAR esté en rango normal y que ambos ojos tengan valores similares
    // const eyesSymmetry = Math.abs(rightEAR - leftEAR) < 0.05; // Máxima diferencia permitida
    return avgEAR >= MIN_OPEN_THRESHOLD && 
          avgEAR <= MAX_OPEN_THRESHOLD;
  }

  // Calculate Eye Aspect Ratio (EAR)
  calculateEAR(eyeLandmarks: any): number {
    const vertical1 = this.euclideanDistance(eyeLandmarks[1], eyeLandmarks[5]);
    const vertical2 = this.euclideanDistance(eyeLandmarks[2], eyeLandmarks[4]);
    const horizontal = this.euclideanDistance(eyeLandmarks[0], eyeLandmarks[3]);

    return (vertical1 + vertical2) / (2.0 * horizontal);
  }

  // Calculate Euclidean distance between two points
  euclideanDistance(point1: any, point2: any): number {
    const dx = point1.x - point2.x;
    const dy = point1.y - point2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Check if face is frontal
  isFrontalFace(landmarks: any[]): boolean {
    // Landmarks clave para validación frontal
    const noseTip = landmarks[4];       // Punta de la nariz
    const leftEyeInner = landmarks[133];// Esquina interna ojo izquierdo
    const rightEyeInner = landmarks[362];// Esquina interna ojo derecho
    const chin = landmarks[152];        // Barbilla
    const leftMouth = landmarks[61];    // Esquina izquierda boca
    const rightMouth = landmarks[291];  // Esquina derecha boca

    // 1. Simetría horizontal
    const eyeDistance = this.euclideanDistance(leftEyeInner, rightEyeInner);
    const mouthWidth = this.euclideanDistance(leftMouth, rightMouth);
    const horizontalSymmetry = () => {
      const diff = Math.abs(eyeDistance - mouthWidth);
      const avg = (eyeDistance + mouthWidth) / 2;
      const relativeDiff = diff / avg;
      const MAX_RELATIVE_DIFF = 0.42;  
      const WIDTH_RATIO = mouthWidth / eyeDistance;
      const VALID_RATIO = WIDTH_RATIO >= 0.7 && WIDTH_RATIO <= 1.5;
      return relativeDiff <= MAX_RELATIVE_DIFF && VALID_RATIO;
    };
    // 2. Proporciones verticales
    const noseToChin = this.euclideanDistance(noseTip, chin);
    const noseToMouth = this.euclideanDistance(noseTip, leftMouth);
    const verticalProportion = (noseToMouth / noseToChin) > 0.3 && (noseToMouth / noseToChin) < 0.5;

    // 3. Alineación nariz-centro
    const faceCenterX = (leftEyeInner.x + rightEyeInner.x) / 2;
    const noseCenterOffset = Math.abs(noseTip.x - faceCenterX);
    const centerAlignment = noseCenterOffset < 0.0552;

    // Validación alternativa de ángulo
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const noseBridge = landmarks[6];
    const angle = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) - 
                  Math.atan2(noseBridge.y - leftEye.y, noseBridge.x - leftEye.x);
    const angleValid = Math.abs(angle) < 0.30;

    return horizontalSymmetry() && verticalProportion && centerAlignment && angleValid;
  }

  // Check if looking at camera
  isLookingAtCamera(landmarks: any[]): boolean {
    // Landmarks de los iris
    const leftIris = landmarks[468];  // Centro iris izquierdo
    const rightIris = landmarks[473]; // Centro iris derecho

    // Landmarks de las esquinas oculares
    const leftEyeOuter = landmarks[33];  // Esquina externa ojo izquierdo
    const leftEyeInner = landmarks[133]; // Esquina interna ojo izquierdo
    const rightEyeOuter = landmarks[362];// Esquina externa ojo derecho
    const rightEyeInner = landmarks[263];// Esquina interna ojo derecho

    // Calcular posición relativa del iris en el ojo
    const leftEyeWidth = this.euclideanDistance(leftEyeOuter, leftEyeInner);
    const leftIrisPosition = Math.abs(leftIris.x - leftEyeOuter.x) / leftEyeWidth;
    
    const rightEyeWidth = this.euclideanDistance(rightEyeInner, rightEyeOuter);
    const rightIrisPosition = Math.abs(rightIris.x - rightEyeInner.x) / rightEyeWidth;

    // Rangos aceptables para mirada frontal (ajustar según testing)
    const MIN_IRIS_POSITION = 0.30;
    const MAX_IRIS_POSITION = 0.60;
    return leftIrisPosition >= MIN_IRIS_POSITION && 
          leftIrisPosition <= MAX_IRIS_POSITION &&
          rightIrisPosition >= MIN_IRIS_POSITION &&
          rightIrisPosition <= MAX_IRIS_POSITION;
  }

  // Calculate pupil movement  👁️
  calculatePupilMovement(framesLandmarks: any[][]): number {
    let movements: number[] = [];

    for (let i = 1; i < framesLandmarks.length; i++) {
        const prevLandmarks = framesLandmarks[i - 1];
        const currLandmarks = framesLandmarks[i];

        if (!prevLandmarks || !currLandmarks) continue;

        // **Movimiento del iris derecho**
        const irisRightPrev = prevLandmarks[468]; // Centro del iris derecho
        const irisRightCurr = currLandmarks[468];

        const eyeRightOuterPrev = prevLandmarks[33]; // Extremo izquierdo del ojo derecho
        const eyeRightInnerPrev = prevLandmarks[133]; // Extremo derecho del ojo derecho

        // Relación de la pupila con los extremos del ojo derecho
        const irisRightRatioPrev = (irisRightPrev.x - eyeRightOuterPrev.x) / 
                                   (eyeRightInnerPrev.x - eyeRightOuterPrev.x);
        const irisRightRatioCurr = (irisRightCurr.x - eyeRightOuterPrev.x) / 
                                   (eyeRightInnerPrev.x - eyeRightOuterPrev.x);

        const eyeRightChange = Math.abs(irisRightRatioCurr - irisRightRatioPrev);

        // **Movimiento del iris izquierdo**
        const irisLeftPrev = prevLandmarks[473]; // Centro del iris izquierdo
        const irisLeftCurr = currLandmarks[473];

        const eyeLeftOuterPrev = prevLandmarks[362]; // Extremo derecho del ojo izquierdo
        const eyeLeftInnerPrev = prevLandmarks[263]; // Extremo izquierdo del ojo izquierdo

        // Relación de la pupila con los extremos del ojo izquierdo
        const irisLeftRatioPrev = (irisLeftPrev.x - eyeLeftOuterPrev.x) / 
                                  (eyeLeftInnerPrev.x - eyeLeftOuterPrev.x);
        const irisLeftRatioCurr = (irisLeftCurr.x - eyeLeftOuterPrev.x) / 
                                  (eyeLeftInnerPrev.x - eyeLeftOuterPrev.x);

        const eyeLeftChange = Math.abs(irisLeftRatioCurr - irisLeftRatioPrev);

        // **Guardar cambio total**
        const totalChange = (eyeRightChange + eyeLeftChange) / 2;
        movements.push(totalChange);
    }

    // **Calcular la varianza del movimiento**
    if (movements.length === 0) return 0.000001; // Evita devolver 0 absoluto

    const mean = movements.reduce((a, b) => a + b, 0) / movements.length;
    const variance = movements.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / movements.length;

    return Math.max(variance, 0.000001); // Evita valores extremadamente bajos
  }

  // Calculate mouth movement 👄
  calculateMouthMovement(framesLandmarks: any[][]): number {
    let movements: number[] = [];

    for (let i = 1; i < framesLandmarks.length; i++) {
        const prevLandmarks = framesLandmarks[i - 1];
        const currLandmarks = framesLandmarks[i];

        if (!prevLandmarks || !currLandmarks) continue;

        // ** Evaluar la apertura de la boca**
        const mouthHeightPrev = this.euclideanDistance(prevLandmarks[13], prevLandmarks[14]); 
        const mouthHeightCurr = this.euclideanDistance(currLandmarks[13], currLandmarks[14]); 
        
        const mouthOpeningChange = Math.abs(mouthHeightCurr - mouthHeightPrev);

        // ** Evaluar el ancho de la boca**
        const mouthWidthPrev = this.euclideanDistance(prevLandmarks[61], prevLandmarks[291]); 
        const mouthWidthCurr = this.euclideanDistance(currLandmarks[61], currLandmarks[291]); 
        
        const mouthWidthChange = Math.abs(mouthWidthCurr - mouthWidthPrev);

        // **Evaluar el movimiento de las comisuras de los labios**
        const mouthCornerRightPrev = prevLandmarks[291];
        const mouthCornerRightCurr = currLandmarks[291];

        const mouthCornerLeftPrev = prevLandmarks[61];
        const mouthCornerLeftCurr = currLandmarks[61];

        const mouthCornerRightChange = this.euclideanDistance(mouthCornerRightPrev, mouthCornerRightCurr);
        const mouthCornerLeftChange = this.euclideanDistance(mouthCornerLeftPrev, mouthCornerLeftCurr);

        // **Cambio total en la boca**
        const totalChange = mouthOpeningChange + mouthWidthChange + (mouthCornerRightChange + mouthCornerLeftChange) / 2;
        movements.push(totalChange);
    }

    // **Calcular la varianza del movimiento**
    if (movements.length === 0) return 0.000001; // Evita devolver 0 absoluto

    const mean = movements.reduce((a, b) => a + b, 0) / movements.length;
    const variance = movements.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / movements.length;

    return Math.max(variance, 0.000001); // Evita valores extremadamente bajos
  }

  getDynamicBlinkThreshold(width: number=1280, height: number=720) {
    const baseThreshold = BlinkThreshold;
    const baseWidth = 1280;
    const baseHeight = 720;

    // Calculates the scale factor according to the resolution
    const scale = Math.min(width / baseWidth, height / baseHeight);

    // If the resolution is lower, lower the threshold proportionally (to a minimum)
    const minThreshold = 0.100;
    const dynamicThreshold = Math.max(baseThreshold * scale, minThreshold);
    
    this.blinkThreshold = dynamicThreshold ?? BlinkThreshold;
  }

  // LANDMARKS MANNAGEMENT
  // Store landmarks for further evaluation
  storeLandmarks(landmarks: any[]) {
    if (this.framesLandmarks.length >= maxFrames) {
      this.framesLandmarks.shift();
    }
    this.framesLandmarks.push(landmarks);
  }

  getLandmarks():any[][]{
    return this.framesLandmarks;
  }

  //PARTIALS DEEPHS MANNAGEMENT
  storePartialDeephs(accepted:boolean) {
    this.acceptedPartialDeeph.push(accepted);
  }

  getPartialDeephs():boolean[]{
    return this.acceptedPartialDeeph;
  }

  validatePartialDeep():boolean{
    return this.acceptedPartialDeeph.reduce((count, value) => count + (value ? 1 : 0), 0) > (this.acceptedPartialDeeph.length / 2) ? true : false;
  }

  //GENERAL DEEPH MANNAGEMENT
  storeGeneralDeephs(accepted:boolean) {
    this.acceptedGeneralDeeph.push(accepted);
  }

  getGeneralDeephs():boolean[]{
    return this.acceptedGeneralDeeph;
  }

  validateGeneralDeep():boolean{
    return this.acceptedGeneralDeeph.reduce((count, value) => count + (value ? 1 : 0), 0) > (this.acceptedGeneralDeeph.length / 2) ? true : false;
  }

  cleanVariables():void{
    this.acceptedGeneralDeeph = [];
    this.acceptedPartialDeeph = [];
    this.framesLandmarks = [];
    this.blinkedTimes = [];
    this.brightnessScores = [];
    this.acceptedGeneralDeephScored = [];
  }
}